import { useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { GoldButton } from '@/components/common/GoldButton';
import { ProfileHeader } from '@/components/profile/ProfileHeader';
import { ProfileMenu } from '@/components/profile/ProfileMenu';
import { ReadingsHistory } from '@/components/profile/ReadingsHistory';
import { TariffGrid } from '@/components/profile/TariffGrid';
import { UnlimitedTariff } from '@/components/profile/UnlimitedTariff';
import {
  formatPackagePrice,
  useCreatePayment,
  usePaymentPackages,
  type PaymentPackage,
} from '@/hooks/api/usePayments';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { t } from '@/i18n';
import anim from '@/styles/animations.module.css';
import styles from './ProfilePage.module.css';

type Selection =
  | { kind: 'package'; id: PaymentPackage['id'] }
  | { kind: 'unlimited' };

export function ProfilePage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const showApiError = useApiErrorToast();
  const { data: packages = [], isLoading, isError } = usePaymentPackages();
  const createPayment = useCreatePayment();

  const unlimitedPackage =
    packages.find((p) => p.id === 'SUB_BUSINESS') ?? null;

  const [selected, setSelected] = useState<Selection | null>(null);

  const defaultReadingPackage = useMemo(
    () =>
      packages.find((p) => p.id === 'READINGS_15') ??
      packages.find((p) => p.type === 'READINGS') ??
      null,
    [packages],
  );

  const effectiveSelection = useMemo((): Selection | null => {
    if (selected) return selected;
    if (defaultReadingPackage) return { kind: 'package', id: defaultReadingPackage.id };
    if (unlimitedPackage) return { kind: 'unlimited' };
    return null;
  }, [selected, defaultReadingPackage, unlimitedPackage]);

  const selectedPackage = useMemo(() => {
    if (!effectiveSelection) return null;
    if (effectiveSelection.kind === 'unlimited') return unlimitedPackage;
    return packages.find((p) => p.id === effectiveSelection.id) ?? null;
  }, [effectiveSelection, packages, unlimitedPackage]);

  const selectedLabelPrice = useMemo(() => {
    if (!selectedPackage) return { label: t.profile.packageFallback, price: '' };
    return {
      label: selectedPackage.label,
      price: formatPackagePrice(selectedPackage.amount, selectedPackage.currency),
    };
  }, [selectedPackage]);

  const handleBuy = async () => {
    if (!selectedPackage) return;
    try {
      const { paymentUrl } = await createPayment.mutateAsync({
        packageId: selectedPackage.id,
      });
      window.location.href = paymentUrl;
    } catch (err) {
      showApiError(err);
    }
  };

  const storeBody = (
    <>
      {isLoading ? (
        <p className={styles.storeHint}>{t.profile.tariffsLoading}</p>
      ) : isError ? (
        <p className={styles.storeHint}>{t.profile.tariffsError}</p>
      ) : (
        <>
          <TariffGrid
            hideTitle
            packages={packages}
            value={
              effectiveSelection?.kind === 'package' ? effectiveSelection.id : null
            }
            onSelect={(pkg) => setSelected({ kind: 'package', id: pkg.id })}
          />
          <UnlimitedTariff
            package={unlimitedPackage}
            selected={effectiveSelection?.kind === 'unlimited'}
            onSelect={() => setSelected({ kind: 'unlimited' })}
          />
        </>
      )}
    </>
  );

  const storePanel = (
    <div className={styles.store}>
      <div className={styles.storeHead}>{t.profile.buySpreads}</div>
      <div className={styles.storeBody}>
        {storeBody}
        <div className={styles.buyWrap}>
          <GoldButton
            disabled={
              isLoading ||
              isError ||
              !selectedPackage ||
              createPayment.isPending
            }
            onClick={() => void handleBuy()}
          >
            {createPayment.isPending
              ? t.profile.payRedirecting
              : t.profile.buyPackage(selectedLabelPrice.label, selectedLabelPrice.price)}
          </GoldButton>
        </div>
      </div>
    </div>
  );

  const profileHeader = <ProfileHeader />;

  if (isDesktop) {
    return (
      <div className={anim.fadeIn}>
        <div className={styles.desktopGrid}>
          <div className={styles.leftCol}>
            {profileHeader}
            <div className={styles.logoutBottom}>
              <ProfileMenu />
            </div>
          </div>
          <div className={styles.rightCol}>
            {storePanel}
            <ReadingsHistory />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={anim.fadeIn}>
      {profileHeader}
      <section className={styles.sec}>
        {storePanel}
        <ReadingsHistory />
        <div className={styles.logoutBottomMobile}>
          <ProfileMenu />
        </div>
      </section>
    </div>
  );
}
